import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AppState,
  type AppStateStatus,
  BackHandler,
  Linking,
  Modal,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { AppButton } from './AppButton';
import { AppLoader } from './AppLoader';
import { AppScreen } from './AppScreen';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import {
  checkAppUpdate,
  currentUpdatePlatform,
  type AppUpdatePrompt,
} from '../services/appUpdateService';

type RequiredUpdate = Extract<AppUpdatePrompt, { status: 'mandatory' | 'optional' }>;
type GateState =
  | { status: 'checking' }
  | { status: 'allowed'; optional: RequiredUpdate | null }
  | { status: 'blocked'; update: RequiredUpdate };

const DEFAULT_MANDATORY_TITLE = 'Update Required';
const DEFAULT_OPTIONAL_TITLE = 'Update Available';

function storeName() {
  return Platform.OS === 'ios' ? 'App Store' : 'Play Store';
}

function defaultMessage(update: RequiredUpdate) {
  if (update.status === 'mandatory') {
    return `A new version of the app is required. Version ${update.installedVersion} is no longer supported — please update to version ${update.targetVersion} or later from the ${storeName()} to continue.`;
  }
  return `A newer version (${update.targetVersion}) is available on the ${storeName()}. You are on version ${update.installedVersion}. Please update to get the latest features and fixes.`;
}

function defaultTitle(update: RequiredUpdate) {
  return update.status === 'mandatory' ? DEFAULT_MANDATORY_TITLE : DEFAULT_OPTIONAL_TITLE;
}

/**
 * On launch (and when returning to the foreground), compares the installed
 * version to the backend store policy. Mandatory updates block the app;
 * optional updates (installed below the published latestVersion) show a
 * single dialog that can be dismissed for the current foreground session
 * and reappears the next time the app is opened.
 */
export function MandatoryUpdateGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(() =>
    currentUpdatePlatform() ? { status: 'checking' } : { status: 'allowed', optional: null },
  );
  const [promptVisible, setPromptVisible] = useState(false);
  const [storeError, setStoreError] = useState<string | null>(null);
  const checking = useRef(false);
  const optionalDismissed = useRef(false);
  const appState = useRef<AppStateStatus>(AppState.currentState);

  const runCheck = useCallback(async () => {
    if (!currentUpdatePlatform() || checking.current) return;
    checking.current = true;
    try {
      const result = await checkAppUpdate();
      if (result.status === 'mandatory') {
        setState({ status: 'blocked', update: result });
        setPromptVisible(true);
        return;
      }
      if (result.status === 'optional') {
        setState({ status: 'allowed', optional: result });
        if (!optionalDismissed.current) setPromptVisible(true);
        return;
      }
      setState({ status: 'allowed', optional: null });
      setPromptVisible(false);
    } catch {
      setState(current =>
        current.status === 'checking' ? { status: 'allowed', optional: null } : current,
      );
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    runCheck();
    const subscription = AppState.addEventListener('change', next => {
      const previous = appState.current;
      appState.current = next;
      // Only a real background (leaving the app) clears the per-launch dismiss
      // flag — transient inactive (e.g. notification shade) must not re-prompt.
      if (next === 'background') {
        optionalDismissed.current = false;
      }
      if (previous !== 'active' && next === 'active') runCheck();
    });
    return () => subscription.remove();
  }, [runCheck]);

  const blocked = state.status === 'blocked';
  useEffect(() => {
    if (!blocked || Platform.OS !== 'android') return undefined;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      BackHandler.exitApp();
      return true;
    });
    return () => subscription.remove();
  }, [blocked]);

  const openStore = (storeUrl: string) => {
    setStoreError(null);
    Linking.openURL(storeUrl).catch(() => {
      setStoreError(
        `Could not open the ${storeName()}. Please update the app from the ${storeName()} manually.`,
      );
    });
  };

  if (state.status === 'checking') {
    return (
      <AppScreen contentStyle={styles.center}>
        <AppLoader message="Starting…" />
      </AppScreen>
    );
  }

  if (state.status === 'blocked') {
    return (
      <MandatoryUpdateScreen
        update={state.update}
        promptVisible={promptVisible}
        storeError={storeError}
        onUpdate={() => openStore(state.update.storeUrl)}
        onCancel={() => setPromptVisible(false)}
        onContinue={() => {
          setPromptVisible(true);
          runCheck();
        }}
      />
    );
  }

  return (
    <>
      {children}
      {state.optional ? (
        <UpdatePromptModal
          update={state.optional}
          visible={promptVisible}
          storeError={storeError}
          dismissible
          onUpdate={() => openStore(state.optional!.storeUrl)}
          onCancel={() => {
            optionalDismissed.current = true;
            setPromptVisible(false);
            setStoreError(null);
          }}
        />
      ) : null}
    </>
  );
}

type ScreenProps = {
  update: RequiredUpdate;
  promptVisible: boolean;
  storeError: string | null;
  onUpdate: () => void;
  onCancel: () => void;
  onContinue: () => void;
};

export function MandatoryUpdateScreen({
  update,
  promptVisible,
  storeError,
  onUpdate,
  onCancel,
  onContinue,
}: ScreenProps) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      title: { ...typography.screenTitle, color: palette.textPrimary, textAlign: 'center' as const },
      message: { ...typography.body, color: palette.textSecondary, textAlign: 'center' as const },
      error: { ...typography.body, color: palette.error, textAlign: 'center' as const },
      card: { backgroundColor: palette.surface, borderColor: palette.border },
    }),
    [palette],
  );
  const title = update.title || defaultTitle(update);
  const message = update.message || defaultMessage(update);

  return (
    <AppScreen contentStyle={styles.center} webLayout="form">
      <View style={styles.content} accessibilityRole="alert">
        <Text style={themed.title}>{title}</Text>
        <Text style={themed.message}>
          You must update the app before you can continue using it.
        </Text>
        {storeError ? <Text style={themed.error}>{storeError}</Text> : null}
        <AppButton title="Update" onPress={onUpdate} style={styles.button} />
        <AppButton
          title="Continue"
          variant="secondary"
          onPress={onContinue}
          accessibilityLabel="Continue to app"
          style={styles.button}
        />
      </View>
      <UpdatePromptModal
        update={update}
        visible={promptVisible}
        storeError={storeError}
        dismissible
        title={title}
        message={message}
        onUpdate={onUpdate}
        onCancel={onCancel}
      />
    </AppScreen>
  );
}

type ModalProps = {
  update: RequiredUpdate;
  visible: boolean;
  storeError: string | null;
  dismissible: boolean;
  title?: string;
  message?: string;
  onUpdate: () => void;
  onCancel: () => void;
};

function UpdatePromptModal({
  update,
  visible,
  storeError,
  dismissible,
  title,
  message,
  onUpdate,
  onCancel,
}: ModalProps) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      title: { ...typography.screenTitle, color: palette.textPrimary, textAlign: 'center' as const },
      message: { ...typography.body, color: palette.textSecondary, textAlign: 'center' as const },
      error: { ...typography.body, color: palette.error, textAlign: 'center' as const },
      card: { backgroundColor: palette.surface, borderColor: palette.border },
    }),
    [palette],
  );
  const resolvedTitle = title || update.title || defaultTitle(update);
  const resolvedMessage = message || update.message || defaultMessage(update);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={dismissible ? onCancel : undefined}
    >
      <View style={styles.overlay}>
        <View style={[styles.card, themed.card]} accessibilityViewIsModal>
          <Text style={themed.title}>{resolvedTitle}</Text>
          <Text style={themed.message}>{resolvedMessage}</Text>
          {storeError ? <Text style={themed.error}>{storeError}</Text> : null}
          <View style={styles.actions}>
            {dismissible ? (
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={onCancel}
                style={styles.action}
              />
            ) : null}
            <AppButton title="Update" onPress={onUpdate} style={styles.action} />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  content: {
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: spacing.lg,
  },
  button: {
    alignSelf: 'stretch',
  },
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    padding: spacing.xl,
    gap: spacing.md,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  action: {
    flex: 1,
  },
});
